import React from 'react';

import SignaturePad from 'react-signature-pad-wrapper';

import { Button } from 'components/Button';
import { EraserIcon } from 'components/Icons/EraserIcon';
import { colors } from 'theme';

import { Placeholder, SignatureContainer } from './styles';

type CustomSignatureFieldProps = {
  value: string | undefined;
  onChange: (dataUrl: string) => void;
  requiredNotFilled: boolean;
  errorMessage?: string;
};

const CustomSignatureField: React.FC<CustomSignatureFieldProps> = ({
  value,
  onChange,
  requiredNotFilled,
}): React.ReactElement => {
  const signaturePadRef = React.useRef<SignaturePad>(null);
  const [isEmpty, setIsEmpty] = React.useState(!value || !value.startsWith('data:image/'));

  const save = (): void => {
    const signaturePad = signaturePadRef.current;
    if (signaturePad) {
      onChange(signaturePad.toDataURL());
    }
  };

  const handleClear = (): void => {
    const signaturePad = signaturePadRef.current;
    if (signaturePad) {
      signaturePad.clear();
      setIsEmpty(true);
      onChange('');
    }
  };

  React.useEffect(() => {
    const signaturePadInstance = signaturePadRef.current?.instance;

    if (!signaturePadInstance) {
      return;
    }

    if (value && value.startsWith('data:image/')) {
      try {
        signaturePadInstance.fromDataURL(value);
        setIsEmpty(false);
      } catch {
        // Failed to load existing signature
      }
    }

    const handleBegin = (): void => {
      setIsEmpty(false);
    };

    const handleEnd = (): void => {
      save();
    };

    signaturePadInstance.addEventListener('beginStroke', handleBegin);
    signaturePadInstance.addEventListener('endStroke', handleEnd);

    return () => {
      signaturePadInstance.removeEventListener('beginStroke', handleBegin);
      signaturePadInstance.removeEventListener('endStroke', handleEnd);
    };
  }, []);

  return (
    <>
      <SignatureContainer
        $error={requiredNotFilled}
        role="button"
        aria-label="signature pad"
        tabIndex={0}
        onClick={(e) => e.stopPropagation()}
      >
        {isEmpty && <Placeholder>Sign here</Placeholder>}
        <SignaturePad
          redrawOnResize
          ref={signaturePadRef}
          options={{ penColor: colors.common.black }}
          height={190}
          canvasProps={{
            style: { width: '100%', height: '100%' },
          }}
        />
      </SignatureContainer>
      <Button
        startIcon={<EraserIcon />}
        sx={{ background: colors.tint.primary.subtle }}
        onClick={(e) => {
          e.stopPropagation();
          handleClear();
        }}
      >
        Clear
      </Button>
    </>
  );
};

export default CustomSignatureField;
